import {
  useGetIdentity,
  useNotification,
  usePermissions,
} from '@refinedev/core'
import type { Invitation, TenantMember } from '@sirius/contracts/admin'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { formatDateTime } from '~/i18n/format'
import type { AdminRole, Identity } from '~/providers/authProvider'
import { errorMessage } from '~/providers/errors'
import { useActiveTenant } from '~/tenancy/activeTenant'
import type { TenantRef } from '~/tenancy/tenantDecision'
import { InviteMemberDialog } from './inviteMemberDialog'
import type { GrantRole, MemberViewer } from './memberActions'
import {
  canManageMember,
  canRevokeInvitation,
  invitationsQueryKey,
  memberRoleOptions,
  membersQueryKey,
  roleChangeConfirmation,
} from './memberActions'

/** A load error of one of the tab's tables. */
function LoadError({
  what,
  error,
}: {
  what: 'members' | 'invitations'
  error: unknown
}) {
  const { t } = useTranslation()
  return (
    <p
      role="alert"
      className="text-destructive text-sm"
    >
      {t(`settingsPage.members.loadError.${what}`, {
        message: errorMessage(error),
      })}
    </p>
  )
}

type RoleChange = { member: TenantMember; role: GrantRole }

/**
 * The members with their roles. A member the viewer may manage
 * (`canManageMember`) gets a role select, which asks before it changes the
 * role, and Remove behind a confirmation; the others (for tenant users:
 * owners and their own row) only show their role.
 */
function MembersTable({
  tenant,
  members,
  viewer,
}: {
  tenant: TenantRef
  /** undefined while they load */
  members: TenantMember[] | undefined
  /** undefined while the viewer loads: no actions yet */
  viewer: MemberViewer | undefined
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const { open } = useNotification()
  // kept while the confirmation closes, so its text stays
  const [roleChange, setRoleChange] = useState<RoleChange | null>(null)
  const [confirmingRole, setConfirmingRole] = useState(false)
  // each row's role select, where focus returns once the confirmation closes
  const roleTriggersRef = useRef(new Map<string, HTMLButtonElement>())
  // also after an error: a member removed or changed meanwhile shows as is
  const refreshMembers = () =>
    queryClient.invalidateQueries({ queryKey: membersQueryKey(tenant.id) })

  const setRole = useMutation({
    mutationFn: ({ member, role }: RoleChange) =>
      client.members.setRole({ id: member.id, role }),
    onSuccess: (member: TenantMember) => {
      open?.({
        type: 'success',
        message: t(`settingsPage.members.roleChange.success.${member.role}`, {
          name: member.name,
          tenant: tenant.name,
        }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('settingsPage.members.roleChange.error'),
        description: errorMessage(error),
      })
    },
    onSettled: refreshMembers,
  })
  const remove = useMutation({
    mutationFn: (member: TenantMember) =>
      client.members.remove({ id: member.id }),
    onSuccess: (member: TenantMember) => {
      open?.({
        type: 'success',
        message: t('settingsPage.members.remove.success', {
          name: member.name,
          tenant: tenant.name,
        }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('settingsPage.members.remove.error'),
        description: errorMessage(error),
      })
    },
    onSettled: refreshMembers,
  })
  const busy = setRole.isPending || remove.isPending

  return (
    <>
      <Table
        aria-label={t('settingsPage.members.tableLabel')}
        className="text-sm"
      >
        <TableHeader>
          <TableRow>
            <TableHead>{t('settingsPage.members.columns.name')}</TableHead>
            <TableHead>{t('settingsPage.members.columns.email')}</TableHead>
            <TableHead>{t('settingsPage.members.columns.role')}</TableHead>
            <TableHead>{t('settingsPage.members.columns.joined')}</TableHead>
            <TableHead>
              <span className="sr-only">
                {t('settingsPage.members.columns.actions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(members ?? []).map((member) => {
            const manageable = canManageMember(viewer, member)
            return (
              <TableRow key={member.id}>
                <TableCell>{member.name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  {manageable ? (
                    <Select
                      value={member.role}
                      onValueChange={(role) => {
                        if (role !== member.role) {
                          setRoleChange({ member, role: role as GrantRole })
                          setConfirmingRole(true)
                        }
                      }}
                    >
                      <SelectTrigger
                        ref={(trigger) => {
                          if (trigger === null) {
                            roleTriggersRef.current.delete(member.id)
                          } else {
                            roleTriggersRef.current.set(member.id, trigger)
                          }
                        }}
                        aria-label={t('settingsPage.members.roleSelectLabel', {
                          name: member.name,
                        })}
                        size="sm"
                        className="w-32"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {memberRoleOptions(member.role).map(
                          ({ role, disabled }) => (
                            <SelectItem
                              key={role}
                              value={role}
                              disabled={disabled || busy}
                            >
                              {t(`settingsPage.roles.${role}`)}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline">
                      {t(`settingsPage.roles.${member.role}`)}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{formatDateTime(member.createdAt)}</TableCell>
                <TableCell className="text-right">
                  {manageable && (
                    <ConfirmDialog
                      trigger={
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                        >
                          {t('settingsPage.members.remove.button')}
                        </Button>
                      }
                      title={t('settingsPage.members.remove.title', {
                        name: member.name,
                        tenant: tenant.name,
                      })}
                      description={t(
                        'settingsPage.members.remove.description',
                        { tenant: tenant.name },
                      )}
                      confirmLabel={t('settingsPage.members.remove.confirm')}
                      destructive
                      onConfirm={() => remove.mutate(member)}
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {roleChange !== null && (
        <ConfirmDialog
          open={confirmingRole}
          onOpenChange={setConfirmingRole}
          returnFocusTo={() =>
            roleTriggersRef.current.get(roleChange.member.id)
          }
          {...roleChangeConfirmation(
            roleChange.member.name,
            roleChange.role,
            tenant.name,
            t,
          )}
          confirmLabel={t(
            `settingsPage.members.roleChange.confirm.${roleChange.role}`,
          )}
          onConfirm={() => setRole.mutate(roleChange)}
        />
      )}
    </>
  )
}

/**
 * The open invitations, or a line that there are none. Revoke behind a
 * confirmation where the viewer may (`canRevokeInvitation`).
 */
function InvitationsTable({
  tenant,
  invitations,
  isPlatformAdmin,
}: {
  tenant: TenantRef
  /** undefined while they load */
  invitations: Invitation[] | undefined
  /** undefined while the viewer's role loads: no actions yet */
  isPlatformAdmin: boolean | undefined
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const { open } = useNotification()
  const revoke = useMutation({
    mutationFn: (invitation: Invitation) =>
      client.invitations.revoke({ id: invitation.id }),
    onSuccess: (invitation: Invitation) => {
      open?.({
        type: 'success',
        message: t('settingsPage.members.revoke.success', {
          email: invitation.email,
        }),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('settingsPage.members.revoke.error'),
        description: errorMessage(error),
      })
    },
    // also after an error: an invitation accepted meanwhile shows as a member
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: invitationsQueryKey(tenant.id),
        }),
        queryClient.invalidateQueries({ queryKey: membersQueryKey(tenant.id) }),
      ]),
  })

  if (invitations?.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('settingsPage.members.noInvitations')}
      </p>
    )
  }
  return (
    <Table
      aria-label={t('settingsPage.members.invitationsTableLabel')}
      className="text-sm"
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t('settingsPage.members.columns.email')}</TableHead>
          <TableHead>{t('settingsPage.members.columns.role')}</TableHead>
          <TableHead>{t('settingsPage.members.columns.invited')}</TableHead>
          <TableHead>{t('settingsPage.members.columns.expires')}</TableHead>
          <TableHead>
            <span className="sr-only">
              {t('settingsPage.members.columns.actions')}
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {(invitations ?? []).map((invitation) => (
          <TableRow key={invitation.id}>
            <TableCell>{invitation.email}</TableCell>
            <TableCell>
              <Badge variant="outline">
                {t(`settingsPage.roles.${invitation.role}`)}
              </Badge>
            </TableCell>
            <TableCell>{formatDateTime(invitation.createdAt)}</TableCell>
            <TableCell>{formatDateTime(invitation.expiresAt)}</TableCell>
            <TableCell className="text-right">
              {isPlatformAdmin !== undefined &&
                canRevokeInvitation(isPlatformAdmin, invitation) && (
                  <ConfirmDialog
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={revoke.isPending}
                      >
                        {t('settingsPage.members.revoke.button')}
                      </Button>
                    }
                    title={t('settingsPage.members.revoke.title', {
                      email: invitation.email,
                    })}
                    description={t('settingsPage.members.revoke.description')}
                    confirmLabel={t('settingsPage.members.revoke.confirm')}
                    destructive
                    onConfirm={() => revoke.mutate(invitation)}
                  />
                )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Who belongs to the host tenant and with which role, the invitations still
 * open, and Invite; role changes, Remove and Revoke where the viewer may
 * (`memberActions.ts`; the API decides). Staff and the tenant's
 * owners/admins (`MEMBERS_RESOURCE`). Both lists are whole (a tenant has at
 * most 100 members and 100 open invitations), so no paging.
 */
export function MembersTab() {
  const { t } = useTranslation()
  const tenant = useActiveTenant()
  const client = useAdminClient()
  const { data: role } = usePermissions<AdminRole>({})
  const { data: identity } = useGetIdentity<Identity>()
  const isPlatformAdmin = role === undefined ? undefined : role === 'admin'
  const viewer =
    isPlatformAdmin === undefined || !identity
      ? undefined
      : { isPlatformAdmin, userId: identity.id }
  const members = useQuery({
    queryKey: membersQueryKey(tenant.id),
    queryFn: () => client.members.list({ tenantId: tenant.id }),
  })
  const invitations = useQuery({
    queryKey: invitationsQueryKey(tenant.id),
    queryFn: () => client.invitations.list({ tenantId: tenant.id }),
  })

  return (
    <div className="max-w-5xl space-y-8">
      <section
        aria-labelledby="members-heading"
        className="space-y-2"
      >
        <div className="flex items-center justify-between gap-2">
          <h3
            id="members-heading"
            className="font-semibold"
          >
            {t('settingsPage.members.heading')}
          </h3>
          {isPlatformAdmin !== undefined && (
            <InviteMemberDialog
              tenant={tenant}
              isPlatformAdmin={isPlatformAdmin}
            />
          )}
        </div>
        {members.isError ? (
          <LoadError
            what="members"
            error={members.error}
          />
        ) : (
          <MembersTable
            tenant={tenant}
            members={members.data}
            viewer={viewer}
          />
        )}
      </section>
      <section
        aria-labelledby="invitations-heading"
        className="space-y-2"
      >
        <h3
          id="invitations-heading"
          className="font-semibold"
        >
          {t('settingsPage.members.openInvitations')}
        </h3>
        {invitations.isError ? (
          <LoadError
            what="invitations"
            error={invitations.error}
          />
        ) : (
          <InvitationsTable
            tenant={tenant}
            invitations={invitations.data}
            isPlatformAdmin={isPlatformAdmin}
          />
        )}
      </section>
    </div>
  )
}
